/**
 * Whether a cell a store already holds is the cell an install delivers — the
 * skip-equal test of a server install (`CellDataStore.set` inside
 * `asInstall`): a re-delivered cell (overlapping fetch padding, a sync
 * refetch, the per-row and the batched read of one window) must cost no
 * write, no stale mark, no event and no ledger bump.
 *
 * Conservative on purpose: `false` means "write it", so a value this cannot
 * prove equal is written exactly as before. Primitives compare by `===`,
 * arrays element-wise, plain objects by the same keys IN THE SAME ORDER with
 * the same values at every depth (a reordered object installs: its order is
 * what a reader serializes). The `@tento/chrono` values an encoded row's
 * decoder builds (`task.v1`, `event.v1`: `DateTime`, `NaiveDate`,
 * `NaiveDateTime`, `NaiveDate.Partial`) compare BY VALUE — a `DateTime` by
 * its instant and its zone's id, the others by class and text (a partial
 * date with its week start) — so a re-delivered task or event cell is
 * skipped too. Any other object (a class instance whose state may live in
 * private fields) is the same only as the same object.
 */

import { DateTime, NaiveDate, NaiveDateTime } from "@tento-chrono";
import type { Option } from "../core/option.ts";
import type { CellStyle } from "../core/dtype.ts";

/** Whether `a` and `b` are the same cell value (see the module note). */
export function sameCellValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!sameCellValue(a[i], b[i])) return false;
    }
    return true;
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return sameChronoValue(a, b);
  // Keys in order, as `Object.entries` lists them, without a `[key, value]` pair per field.
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (let i = 0; i < ka.length; i++) {
    const k = ka[i];
    if (k === undefined || k !== kb[i] || !sameCellValue(a[k], b[k])) return false;
  }
  return true;
}

/** Whether two cell style bags hold the same entries (no bag is not an empty bag). */
export function sameCellStyle(a: Option<CellStyle>, b: Option<CellStyle>): boolean {
  if (a === b) return true;
  if (a == null || b == null || a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false;
  }
  return true;
}

/** Two `@tento/chrono` values of one class, by value (see the module note); `false` for any other pair. */
function sameChronoValue(a: object, b: object): boolean {
  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;
  if (a instanceof DateTime && b instanceof DateTime) return a.mse === b.mse && a.tz.id === b.tz.id;
  if (a instanceof NaiveDate.Partial && b instanceof NaiveDate.Partial) {
    return a.equals(b) && a.weekStart === b.weekStart;
  }
  if (a instanceof NaiveDate.Class && b instanceof NaiveDate.Class) return a.toString() === b.toString();
  if (a instanceof NaiveDateTime && b instanceof NaiveDateTime) return a.toString() === b.toString();
  return false;
}

function isPlainObject(v: object): v is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}
